// apps/console/src/services/auth/keycloak-password-update.ts
//
// On-prem sign-in is Keycloak's direct password grant (keycloak-client.ts). An account
// with ANY pending required action (the temporary password a planning approval issues
// when Keycloak has no SMTP, spec §7.7 and contracts decision 9; an admin reset marked
// temporary; or another enabled action: verify email, configure OTP, update profile)
// gets `invalid_grant` "Account is not fully set up" from that grant, which cannot
// complete it. Keycloak's hosted pages can: this builds an authorization request that
// makes Keycloak sign the user in and run the pending actions, then land on its own
// account page.
//
// The request names Keycloak's BUILT-IN `account-console` client, not `gisify-console`.
// The realm export ships gisify-console's redirect URIs with a never-replaced
// ROOT_DOMAIN_PLACEHOLDER host (manual installs never run render-config.sh, and Keycloak
// never re-imports a realm), so a redirect back to the console would be refused there.
// account-console's redirect `/realms/<realm>/account/*` exists in every imported realm.
// It requires PKCE S256, so a well-formed challenge is sent; the code is never exchanged.
// The user returns to the console's own tab and signs in again.

/** Keycloak's built-in client (created when a realm is imported); its redirect is the account page. */
export const PASSWORD_UPDATE_CLIENT_ID = 'account-console'

const NOT_FULLY_SET_UP = /account is not fully set up/i

/** The token endpoint's error body, or {} when it is not JSON. */
export function parseGrantError(text: string): { error_description?: string } {
  try {
    const value: unknown = JSON.parse(text)
    const description = (value as { error_description?: unknown } | null)?.error_description

    return typeof description === 'string' ? { error_description: description } : {}
  } catch {
    return {}
  }
}

export function isPasswordUpdateRequired(body: { error_description?: string }): boolean {
  return typeof body.error_description === 'string' && NOT_FULLY_SET_UP.test(body.error_description)
}

function base64Url(bytes: Uint8Array): string {
  let binary = ''

  for (const byte of bytes) binary += String.fromCharCode(byte)

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export interface PasswordUpdateUrlInput {
  authority: string
  email: string
}

/** The account page under the realm's issuer: matches account-console's `/realms/<realm>/account/*`. */
export function passwordUpdateRedirectUri(authority: string): string {
  return `${authority.replace(/\/$/, '')}/account/`
}

export function passwordUpdateUrl(i: PasswordUpdateUrlInput): string {
  const challenge = new Uint8Array(32)

  crypto.getRandomValues(challenge)

  const params = new URLSearchParams({
    client_id: PASSWORD_UPDATE_CLIENT_ID,
    response_type: 'code',
    scope: 'openid',
    redirect_uri: passwordUpdateRedirectUri(i.authority),
    code_challenge: base64Url(challenge),
    code_challenge_method: 'S256',
    login_hint: i.email,
  })

  return `${i.authority.replace(/\/$/, '')}/protocol/openid-connect/auth?${params.toString()}`
}
